import { SiteLayout } from "@/components/layout/SiteLayout";
import { SEO } from "@/components/SEO";
import { Link } from "wouter";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { breadcrumbSchema, faqSchema, personSchema, webPageSchema } from "@/lib/seo";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { NetworkNextStep } from "@/components/NetworkNextStep";

const PATH = "/books/your-mind-is-full-of-shit-workbook";
const BOOK_ID = `https://shariqrefai.com${PATH}#book`;
const TITLE = "Your Mind Is Full of Sh*t: The Workbook by Shariq Refai, MD";
const DESCRIPTION =
  "The companion workbook to Your Mind Is Full of Sh*t. Psychiatrist-designed reflection prompts for noticing your own anxiety and overthinking patterns.";
const OG_IMAGE = "/og-ymifos-workbook.jpg";

const faqs = [
  {
    question: "When will the workbook be released?",
    answer:
      "The workbook is scheduled to release alongside or shortly after the main book in 2026. Sign up for notifications to be told the moment it's available.",
  },
  {
    question: "Can I use the workbook without reading the main book?",
    answer:
      "Yes. The workbook is built to stand on its own. Each exercise includes the context you need to use it. The main book deepens the framework, but it's not required.",
  },
  {
    question: "Is this Cognitive Behavioral Therapy (CBT)?",
    answer:
      "The workbook draws on principles from cognitive behavioral therapy, acceptance and commitment therapy, and mindfulness-based work, but it's not CBT itself. It's not a clinical protocol and it's not delivered in a therapeutic context. It's an educational reflection tool written by a psychiatrist.",
  },
  {
    question: "Can I show this workbook to my therapist?",
    answer:
      "Yes. Many readers do. Use it as a conversation starter or as a tool for surfacing patterns you want to discuss in session. Your therapist will know how to integrate it with whatever clinical work you're already doing.",
  },
  {
    question: "Is the workbook a substitute for therapy or psychiatric care?",
    answer:
      "No. The workbook is educational. It's not therapy. It's not a treatment plan. It's not a diagnosis. If you're in crisis or have a clinical condition that needs treatment, please see a licensed clinician.",
  },
  {
    question: "Is the workbook printable?",
    answer:
      "Format details, including print, paperback, and digital fillable versions, will be announced at launch. The email list is the fastest way to be notified.",
  },
  {
    question: "How long does it take to complete the workbook?",
    answer:
      "The workbook is designed for self-paced use. Some readers move through it in a few weeks. Others use the prompts on and off for months. There's no right pace.",
  },
  {
    question: "Will the workbook help with severe anxiety?",
    answer:
      "The workbook is designed for everyday patterns of anxiety, overthinking, and high-functioning stress. For severe anxiety, panic disorder, OCD, PTSD, or any clinical-level condition, the workbook is a supplement to professional care, not a replacement.",
  },
];

const bookJsonLd = {
  "@context": "https://schema.org",
  "@type": "Book",
  "@id": BOOK_ID,
  name: "Your Mind Is Full of Sh*t: The Workbook",
  headline:
    "A psychiatrist's companion guide for noticing patterns in how you think, feel, and act",
  abstract:
    "A structured reflection workbook designed by a board-certified psychiatrist to help readers catch their own anxiety and overthinking patterns in real time.",
  author: { "@id": "https://shariqrefai.com/#person" },
  creator: { "@id": "https://shariqrefai.com/#person" },
  publisher: {
    "@type": "Organization",
    name: "shrinkMD Publishing",
    url: "https://shariqrefai.com/projects",
    memberOf: { "@id": "https://shrinknetwork.com/#organization" },
  },
  bookFormat: "https://schema.org/Paperback",
  datePublished: "2026",
  inLanguage: "en",
  genre: ["Psychology", "Self-Help", "Mental Health", "Workbook"],
  audience: {
    "@type": "Audience",
    audienceType:
      "Adults working on anxiety, overthinking, and self-awareness, alone or alongside a clinician",
  },
  about: [
    { "@type": "Thing", name: "Anxiety reflection exercises" },
    { "@type": "Thing", name: "Overthinking patterns" },
    { "@type": "Thing", name: "Cognitive defusion" },
    { "@type": "Thing", name: "Body awareness" },
    { "@type": "Thing", name: "Self-observation tools" },
  ],
  isPartOf: {
    "@id": "https://shariqrefai.com/books/your-mind-is-full-of-shit#book",
  },
  image: `https://shariqrefai.com${OG_IMAGE}`,
  url: `https://shariqrefai.com${PATH}`,
};

export default function BookWorkbook() {
  const page = webPageSchema({
    path: PATH,
    title: TITLE,
    description: DESCRIPTION,
    image: OG_IMAGE,
    speakableSelectors: [".quick-answer", ".faq"],
    datePublished: "2026-04-01",
    dateModified: "2026-05-08",
    breadcrumbs: [
      { name: "Home", path: "/" },
      { name: "Books", path: "/books" },
      { name: "Your Mind Is Full of Sh*t: The Workbook", path: PATH },
    ],
  });
  const faqLd = faqSchema(faqs);
  return (
    <SiteLayout>
      <SEO
        title={TITLE}
        ogTitle="Your Mind Is Full of Sh*t: The Workbook"
        twitterTitle="Your Mind Is Full of Sh*t: The Workbook"
        description={DESCRIPTION}
        url={PATH}
        image={OG_IMAGE}
        type="book"
        keywords="Your Mind Is Full of Sh*t Workbook, anxiety workbook, overthinking workbook, psychiatrist workbook, reflection guide"
        schema={[
          personSchema,
          page,
          breadcrumbSchema([
            { name: "Home", path: "/" },
            { name: "Books", path: "/books" },
            { name: "Your Mind Is Full of Sh*t: The Workbook", path: "/books/your-mind-is-full-of-shit-workbook" },
          ]),
          bookJsonLd,
          faqLd,
        ]}
      />

      {/* Hero */}
      <section className="hero-book pt-24 pb-16 md:pt-32 md:pb-20 px-4 md:px-8 bg-muted/30 border-b border-border/50">
        <div className="container mx-auto max-w-6xl">
          <Breadcrumbs
            items={[
              { name: "Home", href: "/" },
              { name: "Books", href: "/books" },
              { name: "The Workbook" },
            ]}
          />
        </div>
        <div className="container mx-auto max-w-6xl grid lg:grid-cols-[1.4fr_1fr] gap-12 lg:gap-16 items-start">
          <div className="flex flex-col gap-6">
            {/* Credibility eyebrow */}
            <p className="text-xs md:text-sm font-bold uppercase tracking-[0.25em] text-accent">
              Book · Shariq Refai, MD, MBA · Board-Certified Psychiatrist
            </p>
            <span className="status-pill self-start text-xs font-bold tracking-widest uppercase text-accent border border-accent/40 px-3 py-1.5 rounded-sm">
              Companion · Coming Soon · 2026
            </span>
            <h1 className="text-3xl md:text-5xl font-serif text-primary leading-[1.1] tracking-tight">
              Your Mind Is Full of Sh*t: The Workbook
            </h1>
            <p className="lede text-xl md:text-2xl font-serif italic text-secondary leading-snug">
              Reading about your patterns is one thing. Catching them in real time is another. The workbook turns the framework from <em>Your Mind Is Full of Sh*t</em> into structured exercises and reflection prompts you can use in a real week, on a real schedule.
            </p>
            <div className="cta-row flex flex-wrap gap-4 mt-2">
              <Link
                href="/contact"
                className="btn-primary inline-flex items-center justify-center bg-primary text-primary-foreground hover:bg-primary/90 transition-colors px-6 py-3 text-sm font-medium tracking-wide rounded-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 focus-visible:ring-offset-background"
              >
                Notify Me at Launch
              </Link>
              <Link
                href="/books/your-mind-is-full-of-shit"
                className="btn-secondary inline-flex items-center justify-center border border-primary text-primary hover:bg-primary hover:text-primary-foreground transition-colors px-6 py-3 text-sm font-medium tracking-wide rounded-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 focus-visible:ring-offset-background"
              >
                View the Main Book
              </Link>
            </div>
          </div>
          <div className="flex justify-center">
            <div className="w-full max-w-sm aspect-[2/3] bg-[#EFECE6] border border-border flex flex-col items-center justify-center p-8 text-center shadow-xl">
              <span className="text-sm tracking-widest uppercase text-muted-foreground">Cover coming soon</span>
              <h3 className="font-serif text-2xl text-primary/40 mt-4">The Workbook</h3>
            </div>
          </div>
        </div>
      </section>

      {/* Quick Answer */}
      <section className="quick-answer py-12 md:py-16 px-4 md:px-8 bg-background">
        <div className="container mx-auto max-w-3xl">
          <h2 className="text-2xl md:text-3xl font-serif text-primary mb-4">Quick Answer</h2>
          <p className="text-lg text-secondary font-light leading-relaxed">
            <em>Your Mind Is Full of Sh*t: The Workbook</em> is the companion guide to Dr. Shariq Refai's main book. It's a structured set of psychiatrist-designed reflection exercises, observation prompts, and tracking tools to help you catch your own anxiety and overthinking patterns as they happen. The workbook is educational. It's not therapy. You can use it alone or alongside the main book.
          </p>
        </div>
      </section>

      {/* Why a Workbook */}
      <section className="py-12 md:py-16 px-4 md:px-8 bg-muted/20 border-y border-border/40">
        <div className="container mx-auto max-w-3xl prose prose-lg prose-headings:font-serif prose-headings:font-normal prose-headings:text-primary prose-p:font-light prose-p:text-secondary max-w-none">
          <h2>Why a Workbook</h2>
          <p>Reading about a pattern isn't the same as recognizing it in your own life on a Tuesday at 11 a.m. when the chest tightness shows up before the thought does. The main book gives you the framework. The workbook is what helps the framework land in your real week.</p>
          <p>I wrote the workbook because patients kept asking me the same question. "I read the book. I get it. Now what do I actually do." This is the answer.</p>
          <p>It's not a journal with prompts you've seen before. It's the kind of structured observation work I'd run with a patient in a session, written down in a form you can use yourself. Where in your body does your weather live? Which of the four lies has the loudest grip on you right now? What's in your invisible backpack that doesn't belong there? Each exercise is small. The cumulative effect is what changes the relationship.</p>

          <h2>How It Relates to the Main Book</h2>
          <p>The main book explains the why. The workbook focuses on the what now. The main book walks you through the four lies and the wiring underneath them. The workbook helps you map those lies onto your own life.</p>
          <p>You don't have to read the main book first. The workbook is built to stand on its own. The exercises include the context you need to use them. If you want the deeper conceptual work, the main book is there. If you want to skip ahead to the practice, the workbook is the place to start.</p>
        </div>
      </section>

      {/* What's Inside */}
      <section className="py-12 md:py-16 px-4 md:px-8 bg-background">
        <div className="container mx-auto max-w-3xl">
          <h2 className="text-2xl md:text-3xl font-serif text-primary mb-4">What's Inside</h2>
          <p className="text-lg text-secondary font-light leading-relaxed mb-8">
            The workbook runs in five sections, each built around a specific kind of self-observation work.
          </p>
          <div className="space-y-8">
            {[
              {
                h: "Section 1, Catch the Thought",
                p: "Exercises for noticing the moment a thought arrives, separating the thought from the thinking, and putting a half second of space between you and the loop. This is the foundation. Most of the rest of the work depends on it.",
              },
              {
                h: "Section 2, Read the Body",
                p: "Tools for noticing where your nervous system holds its weather. Locating the buzz, the tightness, the heat, the pressure. Naming what your body is doing in plain language. The body settles when it has language for what it's feeling.",
              },
              {
                h: "Section 3, Map Your Lies",
                p: "Reflection prompts that help you find which of the four lies, control, productivity, positivity, or happiness, has the loudest grip on you right now. The lies show up differently for everyone. The work is recognizing your specific version.",
              },
              {
                h: "Section 4, Track the Loop",
                p: "Worksheets for breaking down the thought-body-meaning-behavior loop. When did the worry start. What was the body already doing. What story did the brain build. What did you do next. Naming the steps separately is what gives you choice points you didn't have before.",
              },
              {
                h: "Section 5, Practice the Reset",
                p: "A 7-day reflection plan that walks you through the core practices in compressed form. Not a treatment plan. Not a clinical protocol. A guided introduction to the daily-life version of the work.",
              },
            ].map((s) => (
              <div key={s.h}>
                <h3 className="text-xl font-serif text-primary mb-2">{s.h}</h3>
                <p className="text-secondary font-light leading-relaxed">{s.p}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Who It's For */}
      <section className="py-12 md:py-16 px-4 md:px-8 bg-muted/20 border-y border-border/40">
        <div className="container mx-auto max-w-3xl">
          <h2 className="text-2xl md:text-3xl font-serif text-primary mb-4">Who This Workbook Is For</h2>
          <p className="text-lg text-secondary font-light leading-relaxed mb-4">
            The workbook is for the reader who wants to do the work, not just read about it. The person who has read the main book and is looking for the practice. The person who hasn't read the main book and wants to start with hands-on observation. The person who's working with a therapist and wants something they can do between sessions. The person who's curious about how their own patterns actually run and wants a structured way to find out.
          </p>
          <p className="text-secondary font-light leading-relaxed">
            It may also serve as a general reflection tool that clinicians can mention to interested readers, with the appropriate disclaimers; it is not itself a clinical instrument.
          </p>
        </div>
      </section>

      {/* How to Use */}
      <section className="py-12 md:py-16 px-4 md:px-8 bg-background">
        <div className="container mx-auto max-w-3xl">
          <h2 className="text-2xl md:text-3xl font-serif text-primary mb-4">How to Use the Workbook</h2>
          <p className="text-lg text-secondary font-light leading-relaxed mb-6">Three honest ways to work with it.</p>
          <div className="space-y-5 text-secondary font-light leading-relaxed">
            <p><strong className="text-primary">Solo, paced.</strong> One section a week. Skim the main book chapter that matches the section if you have it. Use the prompts in your own time. Write what's true, not what sounds good.</p>
            <p><strong className="text-primary">Solo, intensive.</strong> One section a day for five days, then the 7-day reset plan. Best for readers who want a focused stretch of pattern observation work.</p>
            <p><strong className="text-primary">With a clinician.</strong> Bring the workbook to your therapist or psychiatrist if you have one. Use the exercises as a way to surface material to talk about in session. The workbook is educational, not clinical, but it can be a useful conversation starter with the right professional in the room.</p>
          </div>
        </div>
      </section>

      {/* Series */}
      <section className="py-12 md:py-16 px-4 md:px-8 bg-muted/20 border-y border-border/40">
        <div className="container mx-auto max-w-3xl">
          <h2 className="text-2xl md:text-3xl font-serif text-primary mb-4">The Series</h2>
          <p className="text-lg text-secondary font-light leading-relaxed">
            The workbook is the second book in a three-book set. The main book,{" "}
            <Link href="/books/your-mind-is-full-of-shit" className="text-accent underline hover:no-underline focus:outline-none focus-visible:ring-2 focus-visible:ring-accent rounded-sm">
              <em>Your Mind Is Full of Sh*t</em>
            </Link>
            , is the framework. The follow-up,{" "}
            <Link href="/books/the-havoc-in-your-head" className="text-accent underline hover:no-underline focus:outline-none focus-visible:ring-2 focus-visible:ring-accent rounded-sm">
              <em>The Havoc in Your Head</em>
            </Link>
            , is the in-the-moment response when the patterns actually hit. Each book stands on its own. Together they form a working library for the modern brain.
          </p>
        </div>
      </section>

      {/* Notify CTA */}
      <section id="notify" className="cta-final py-16 md:py-20 px-4 md:px-8 bg-primary text-primary-foreground">
        <div className="container mx-auto max-w-3xl text-center">
          <h2 className="text-2xl md:text-3xl font-serif mb-4">Be Notified When the Workbook Is Available</h2>
          <p className="text-lg font-light leading-relaxed mb-8 opacity-90">
            One short email when the workbook is ready. No daily newsletter. No spam.
          </p>
          <Link
            href="/contact"
            className="inline-flex items-center justify-center bg-accent text-white hover:bg-accent/90 transition-colors px-8 py-3 text-sm font-medium tracking-wide rounded-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-primary"
          >
            Request Launch Notification
          </Link>
        </div>
      </section>

      {/* FAQ */}
      <section className="faq py-12 md:py-16 px-4 md:px-8 bg-background">
        <div className="container mx-auto max-w-3xl">
          <h2 className="text-2xl md:text-3xl font-serif text-primary mb-8">Frequently Asked Questions</h2>
          <Accordion type="single" collapsible className="w-full">
            {faqs.map((f, i) => (
              <AccordionItem key={i} value={`item-${i}`}>
                <AccordionTrigger className="text-left font-serif text-lg">{f.question}</AccordionTrigger>
                <AccordionContent className="text-muted-foreground font-light leading-relaxed text-base">
                  {f.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* Disclaimer */}
      <section className="py-12 px-4 md:px-8 bg-muted/30 border-t border-border/50">
        <div className="container mx-auto max-w-3xl">
          <h3 className="text-lg font-serif text-primary mb-3">Important Educational Disclaimer</h3>
          <p className="text-sm text-secondary font-light leading-relaxed">
            The content on this website is provided for general educational and informational purposes only and is not medical advice or a substitute for individualized psychiatric or medical care.
          </p>
          <p className="text-sm text-secondary font-light leading-relaxed mt-3">
            Viewing this website, reading its content, or submitting information through the website does not establish a physician-patient relationship.
          </p>
          <p className="text-sm text-secondary font-light leading-relaxed mt-3">
            The exercises and prompts in this workbook are strictly educational. They are not clinical exercises, a therapeutic protocol, or any form of individualized medical guidance. They're designed for self-reflection and pattern awareness only.
          </p>
          <ul className="text-sm text-secondary font-light leading-relaxed mt-3 list-disc pl-5 space-y-2">
            <li>The workbook does not promise specific outcomes, symptom reduction, or clinical results.</li>
            <li>The workbook is not a substitute for professional mental health care.</li>
            <li>Readers should consult their own licensed clinicians regarding diagnosis or treatment.</li>
            <li>The workbook is intended for general adult audiences and is not intended for use by minors without appropriate adult guidance.</li>
            <li>If you are in crisis, call or text 988 in the United States, call 911, or go to the nearest emergency room.</li>
          </ul>
        </div>
      </section>

      {/* Your next step in The Shrink Network */}
      <section className="py-16 md:py-20 px-4 md:px-8 bg-background border-t border-border/50">
        <div className="container mx-auto max-w-3xl">
          <NetworkNextStep destinations={["shrinq", "shrinkopedia", "shrinkmd"]} />
        </div>
      </section>
    </SiteLayout>
  );
}
